import classNames from "classnames";
import React, { useMemo } from "react";
import FocusLock from "react-focus-lock";
import { useElementId } from "../../hooks/use-element-id";
import { IconButton } from "../button";
import { IconCancel } from "../icons";
import { Overlay } from "../overlay/overlay";
import { OverlayProps } from "../overlay/overlay.types";
import { Text } from "../text";
import { DIALOG_CSS_CLASSES as CSS_CLASSES } from "./dialog-css-classes";

export interface DialogToOverlayPassThroughProps
  extends Pick<
    OverlayProps,
    | "open"
    | "onClose"
    | "closeOnOutsideClick"
    | "usePortal"
    | "lazyPortal"
    | "portalChildrenDidMount"
    | "portalContainer"
    | "portalClassName"
    | "showBackdrop"
    | "backdropClassName"
    | "preserveDomElement"
  > {}

export type AlignProps = "left" | "center" | "right";

export interface DialogProps extends DialogToOverlayPassThroughProps {
  /**
   * A unique id for the Dialog
   */
  id?: string;

  /**
   * `id` reference of an HTML element that contains the label text for this Dialog.
   */
  labelledBy?: string;

  /**
   * Text to be used for the Dialog label. Use this instead of `labelledBy` if needed.
   */
  describedBy?: string;

  /**
   * Content to be displayed within the Dialog header.
   */
  header?: JSX.Element | string;

  /**
   * Content to be displayed within the main Dialog body.
   *
   * Youse this for a complete custom look for the Dialog content, that includes custom styling for the header and/or
   * custom styling or placement for the Dialog actions. In this case you would not use the `header` and `actions` props.
   */
  children: JSX.Element;

  /**
   * A list of CTA component (`Button` or `Link`) to ac as the action target for the Dialog.
   */
  actions?: JSX.Element[];

  /**
   * Options to align Dialog actions.
   *
   * @default right
   */
  actionsAlign?: AlignProps;

  /**
   *
   * `modal` is a `Dialog` specific prop to conveniently opt between a **modal** and **non-modal** dialog - without
   * having to worry about the low level `closeOnOutsideClick` and `showBackdrop` props. These two low level props are
   * actually relayed on to the underlying `Overlay` component that `Dialog` is built upon. And they are exposed to
   * implement custom behavior not covered by the modal prop.
   *
   * When `modal={true}`, then `closeOnOutsideClick={false}` and `showBackdrop={true}` always, irrespective of whether
   * they are explicitly set or not.
   *
   * When `modal={false}`, then `closeOnOutsideClick={true}` and `showBackdrop={false}` only if they are not explicitly
   * set. If they are explicitly set then those values are used instead.
   *
   * @default false
   */
  modal?: boolean;

  /**
   * use this prop to force the Dialog close button (X) to be hidden.
   * Note: This is not recommended by standard A11Y guidelines.
   *
   * @default false
   */
  hideClose?: boolean;

  /**
   * Use this prop to control if focus is trapped within the elements of the Dialog box.
   * This is recommended from an A11Y perspective. So this is `true` by default.
   * However, there may be cases when this needs to be turned off.
   *
   * @default true;
   */
  trapFocus?: boolean;

  /**
   * Use this to control styling for the Dialog content root element.
   */
  className?: string;

  /**
   * Use to override styling for the underlying `Overlay` component that `Dialog` is built upon.
   */
  overlayClassName?: string;

  /**
   * Override props for the underlying `Overlay` component that `Dialog` is built upon.
   */
  overlayRootProps?: OverlayProps["rootProps"];
}

export const Dialog = ({
  /* Own Props */
  id,
  labelledBy,
  describedBy,
  header,
  children,
  actions,
  actionsAlign = "right",
  modal = false,
  hideClose = false,
  trapFocus = true,
  className,
  overlayClassName: overlayClassNameProp,
  overlayRootProps,
  /* Overlay Pass-through Props */
  open,
  onClose,
  closeOnOutsideClick = true,
  usePortal,
  lazyPortal,
  portalChildrenDidMount,
  portalContainer,
  portalClassName,
  showBackdrop = false,
  backdropClassName,
  preserveDomElement,
  /* Rest props */
  ...restProps
}: DialogProps) => {
  const dialogId = useElementId(id, "dialog");
  const headerId = `${dialogId}-header`;
  const bodyId = `${dialogId}-body`;

  const dialogClassName = classNames(CSS_CLASSES.DIALOG_ROOT, className);

  const dialogActionsClassName = classNames(CSS_CLASSES.DIALOG_ACTIONS, {
    "text-left": actionsAlign === "left",
    "text-center": actionsAlign === "center",
    "text-right": actionsAlign === "right",
  });

  const overlayClassName = classNames(CSS_CLASSES.DIALOG_OVERLAY, overlayClassNameProp);

  const ariaModal = modal === true ? true : undefined;

  const ariaLabelledBy = labelledBy || (header && headerId) || bodyId;

  // If `ariaLabelledBy` ends up pointing to the dialog body, then don't apply `ariaDescribedBy`
  const ariaDescriedBy = describedBy || ariaLabelledBy !== bodyId ? bodyId : undefined;

  let mayBeCloseButton: JSX.Element = null;
  if (!hideClose) {
    mayBeCloseButton = (
      <div className={CSS_CLASSES.DIALOG_CLOSE_BUTTON}>
        <IconButton
          label="close"
          variant="ghost"
          size="small"
          icon={<IconCancel color="currentcolor" className="text-black" />}
          onClick={onClose}
          autoFocus={!trapFocus ? true : undefined}
        />
      </div>
    );
  }

  let mayBeHeader: JSX.Element = null;
  const mayBeHeaderCommonProps = {
    id: headerId,
    className: CSS_CLASSES.DIALOG_HEADER,
  };
  if (header) {
    if (typeof header === "string") {
      mayBeHeader = (
        <Text {...mayBeHeaderCommonProps} variant="heading-04">
          {header}
        </Text>
      );
    } else {
      mayBeHeader = <div {...mayBeHeaderCommonProps}>{header}</div>;
    }
  }

  let mayBeActions: JSX.Element = null;
  if (actions && actions.length > 0) {
    mayBeActions = (
      <div className={CSS_CLASSES.DIALOG_ACTIONS_WRAPPER}>
        {/* @todo [BDS-491] replace this with ContentSeparator component */}
        <hr className="border-gray-300 border-0 border-b-1 m-0" />
        <div className={dialogActionsClassName}>
          {actions.reverse().map((action, i) => (
            <span key={`dialog-action-${i}`}>{action}</span>
          ))}
        </div>
      </div>
    );
  }

  const dialogContent = useMemo(() => {
    const content = (
      <>
        {mayBeCloseButton}
        {mayBeHeader}
        <div className={CSS_CLASSES.DIALOG_BODY} id={bodyId}>
          {children}
        </div>
        {mayBeActions}
      </>
    );

    if (trapFocus) {
      return <FocusLock returnFocus={true}>{content}</FocusLock>;
    } else {
      return content;
    }
  }, [bodyId, children, mayBeActions, mayBeCloseButton, mayBeHeader, trapFocus]);

  return (
    <Overlay
      open={open}
      onClose={onClose}
      closeOnOutsideClick={modal === true ? false : closeOnOutsideClick}
      closeOnEscapeKey={true}
      usePortal={usePortal}
      lazyPortal={lazyPortal}
      preserveDomElement={preserveDomElement}
      portalChildrenDidMount={portalChildrenDidMount}
      portalContainer={portalContainer}
      portalClassName={portalClassName}
      showBackdrop={modal || showBackdrop}
      backdropClassName={backdropClassName}
      clipContainer={true}
      makeOthersInert={false}
      className={overlayClassName}
      rootProps={{ tabIndex: 0, ...overlayRootProps }}
    >
      <div
        className={dialogClassName}
        role="dialog"
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescriedBy}
        aria-modal={ariaModal}
        {...restProps}
      >
        {dialogContent}
      </div>
    </Overlay>
  );
};
